import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Play, Upload, Filter, Tag, Loader2, Trash2, Pencil, BarChart2, Clock, LayoutList, LayoutGrid, Calendar, ChevronDown } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';
import UploadClipForm from '../components/UploadClipForm';
import EditClipModal from '../components/EditClipModal';
import PlayerStatsModal from '../components/PlayerStatsModal';
import PlayerStatsTab from '../components/PlayerStatsTab';
import { trainingAPI, videosAPI, BACKEND_URL } from '../services/api';
import TeamGate from '../components/TeamGate';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../hooks/use-toast';

const TrainingSession = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [session, setSession] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [showAddClipModal, setShowAddClipModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [statsVideo, setStatsVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('session'); // 'session' or 'stats'
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideoToAdd, setSelectedVideoToAdd] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const { hasPermission } = useTeam();
  const canManage = hasPermission('manage_training_sessions');
  const canEdit = hasPermission('edit_clip_details');
  const canStats = hasPermission('enter_player_stats');

  const [filters, setFilters] = useState({
    down: 'all',
    formation: 'all',
    personnel: 'all',
    result: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Annotation State
  const [annotations, setAnnotations] = useState([]);

  const fetchSessionData = async (retryCount = 0) => {
    if (retryCount === 0) setLoading(true);
    setError(null);
    try {
      const [sessionRes, videosRes] = await Promise.all([
        trainingAPI.getSession(id),
        trainingAPI.listSessionVideos(id)
      ]);
      setSession(sessionRes.data);
      setVideos(videosRes.data);
      setFilteredVideos(videosRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load training session:', err);

      // Auto-retry on network errors or 503s (server restarting)
      if (retryCount < 3 && (err.message === "Network Error" || err.response?.status >= 500)) {
        console.log(`Retrying connection (${retryCount + 1}/3)...`);
        setTimeout(() => fetchSessionData(retryCount + 1), 2000);
        return;
      }

      setError(err.message || 'Failed to load session');
      toast({
        title: "Connection Issue",
        description: "Could not connect to server. It might be restarting.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSessionData();
    }
  }, [id]);

  // Apply filters
  useEffect(() => {
    let filtered = [...videos];

    if (filters.down !== 'all') {
      filtered = filtered.filter(v => v.play_data?.down === parseInt(filters.down));
    }
    if (filters.formation !== 'all') {
      filtered = filtered.filter(v => v.play_data?.formation === filters.formation);
    }
    if (filters.personnel !== 'all') {
      filtered = filtered.filter(v => v.play_data?.personnel === filters.personnel);
    }
    if (filters.result !== 'all') {
      filtered = filtered.filter(v => v.play_data?.result === filters.result);
    }

    setFilteredVideos(filtered);
  }, [filters, videos]);

  const currentVideo = filteredVideos[currentVideoIndex];
  // Backend response might have 'video' object or flattened fields.
  // Assuming video_url comes from the joined video object or constructed on backend
  const rawVideoUrl = currentVideo?.video?.url || currentVideo?.url || currentVideo?.video_url;
  const currentVideoUrl = rawVideoUrl && (rawVideoUrl.startsWith('http') || rawVideoUrl.startsWith('blob:'))
    ? rawVideoUrl
    : (rawVideoUrl ? `${BACKEND_URL}${rawVideoUrl}` : null);

  // Fetch annotations when video changes
  useEffect(() => {
    const fetchAnnotations = async () => {
      if (!currentVideo) return;
      try {
        const res = await trainingAPI.listAnnotations(id, currentVideo.id);
        setAnnotations(res.data);
      } catch (err) {
        console.error("Failed to load annotations:", err);
      }
    };
    fetchAnnotations();
  }, [currentVideo, id]);

  const handleAnnotationCreate = async (data) => {
    if (!currentVideo) return;
    try {
      await trainingAPI.createAnnotation(id, currentVideo.id, data);

      toast({
        title: "Note Saved",
        description: "Your annotation has been saved.",
      });

      // Refresh
      const res = await trainingAPI.listAnnotations(id, currentVideo.id);
      setAnnotations(res.data);
    } catch (err) {
      console.error("Failed to save annotation:", err);
      toast({
        title: "Error",
        description: "Failed to save note.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    if (!currentVideo) return;
    try {
      await trainingAPI.deleteAnnotation(id, currentVideo.id, annotationId);

      toast({ title: "Note Deleted" });

      // Refresh
      const res = await trainingAPI.listAnnotations(id, currentVideo.id);
      setAnnotations(res.data);
    } catch (err) {
      console.error("Failed to delete annotation:", err);
      toast({
        title: "Error",
        description: "Failed to delete note.",
        variant: "destructive"
      });
    }
  };

  const handleVideoEnd = () => {
    if (isPlayingAll && currentVideoIndex < filteredVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    } else {
      setIsPlayingAll(false);
    }
  };

  const handlePlayAll = () => {
    setCurrentVideoIndex(0);
    setIsPlayingAll(true);
  };

  const handlePreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  const handleNextVideo = () => {
    if (currentVideoIndex < filteredVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideos(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const handleBulkTag = () => {
    // Mock bulk tagging
    console.log('Bulk tagging videos:', selectedVideos);
    setSelectedVideos([]);
  };

  const handleOpenAddClipModal = async () => {
    try {
      const response = await videosAPI.list();
      setAvailableVideos(response.data);
      setShowAddClipModal(true);
    } catch (error) {
      console.error('Failed to load videos:', error);
    }
  };

  const handleDeleteClip = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this clip?')) return;

    try {
      await trainingAPI.deleteVideo(id, videoId);

      toast({
        title: "Clip deleted",
        description: "The video clip has been removed from the session."
      });

      // Refresh list
      const videosRes = await trainingAPI.listSessionVideos(id);
      setVideos(videosRes.data);
      setFilteredVideos(videosRes.data);

      // Reset selection if deleted video was playing
      if (filteredVideos[currentVideoIndex]?.id === videoId) {
        setCurrentVideoIndex(0);
      }
    } catch (error) {
      console.error('Failed to delete clip:', error);
      toast({
        title: "Error",
        description: "Failed to delete clip. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSuccessUpdate = async () => {
    // 1. Close modal first to reset state
    setEditingVideo(null);

    // 2. Refresh list
    const videosRes = await trainingAPI.listSessionVideos(id);
    setVideos(videosRes.data);
    setFilteredVideos(videosRes.data);

    // 3. Update current video if it was the one edited
    if (editingVideo && currentVideo && editingVideo.id === currentVideo.id) {
      // No explicit set needed as derived from index, but good to know
    }
  };

  const handleAddClip = async () => {
    if (!selectedVideoToAdd) return;

    try {
      await trainingAPI.addVideo({
        session_id: id,
        video_id: selectedVideoToAdd,
        title: availableVideos.find(v => v.id === selectedVideoToAdd)?.title || 'Clip'
      });

      // Reload videos
      const videosRes = await trainingAPI.listSessionVideos(id);
      setVideos(videosRes.data);
      setFilteredVideos(videosRes.data);
      setShowAddClipModal(false);
      setSelectedVideoToAdd(null);
    } catch (error) {
      console.error('Failed to add clip:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <TeamGate title="Training Session">
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </TeamGate>
    );
  }

  if (error) {
    return (
      <TeamGate title="Training Session">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="bg-red-500/10 p-4 rounded-full mb-4">
            <Trash2 className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={fetchSessionData} className="bg-blue-600 hover:bg-blue-700">
            Try Again
          </Button>
          <Button variant="ghost" className="mt-2 text-gray-500" onClick={() => navigate('/training')}>
            Back to Training
          </Button>
        </div>
      </TeamGate>
    );
  }

  if (!session) return null;

  // Get unique values for filters
  const formations = [...new Set(videos.map(v => v.play_data?.formation).filter(Boolean))];
  const personnels = [...new Set(videos.map(v => v.play_data?.personnel).filter(Boolean))];
  const results = [...new Set(videos.map(v => v.play_data?.result).filter(Boolean))];

  return (
    <TeamGate title="Training Session">
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/training')}
            className="text-gray-400 hover:text-white mb-3 md:mb-4 -ml-2 text-xs md:text-sm"
          >
            <ArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2" />
            {t('videoDetail.backToVideos')}
          </Button>

          {/* Mobile Premium Header */}
          <div className="md:hidden">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1a1a2e] via-[#16162a] to-[#0f0f1a] border border-gray-800/50 p-4 mb-4">
              {/* Decorative gradient orbs */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-600/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-purple-600/10 rounded-full blur-2xl" />

              <div className="relative z-10">
                <h1 className="text-lg font-bold text-white mb-1 leading-tight">{session.name}</h1>
                {session.description && (
                  <p className="text-xs text-gray-400 mb-3 line-clamp-2">{session.description}</p>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-blue-500/15 text-blue-400 px-2 py-1 rounded-full border border-blue-500/20">
                    <Calendar className="w-3 h-3" />
                    {new Date(session.date).toLocaleDateString()}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-purple-500/15 text-purple-400 px-2 py-1 rounded-full border border-purple-500/20">
                    <Play className="w-3 h-3" />
                    {filteredVideos.length} {t('training.clips')}
                  </span>
                </div>
                {canManage && (
                  <Button onClick={handleOpenAddClipModal} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white text-sm h-9 rounded-lg shadow-lg shadow-blue-600/20">
                    <Upload className="w-4 h-4 mr-2" />
                    {t('training.addClips')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Header (unchanged) */}
          <div className="hidden md:flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 leading-tight">{session.name}</h1>
              <p className="text-base text-gray-400">{new Date(session.date).toLocaleDateString()} • {filteredVideos.length} {t('training.clips')}</p>
              {session.description && (
                <p className="text-base text-gray-500 mt-2">{session.description}</p>
              )}
            </div>
            <div className="flex shrink-0">
              {canManage && (
                <Button onClick={handleOpenAddClipModal} className="w-auto bg-blue-600 hover:bg-blue-700">
                  <Upload className="w-4 h-4 mr-2" />
                  {t('training.addClips')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 md:gap-4 mb-4 md:mb-6 border-b border-gray-800 pb-0 md:pb-1">
          <button
            className={`pb-2.5 md:pb-3 px-3 md:px-1 text-xs md:text-sm font-medium transition-all relative rounded-t-lg ${activeTab === 'session' ? 'text-blue-400 bg-blue-500/5 md:bg-transparent' : 'text-gray-500 hover:text-gray-300'}`}
            onClick={() => setActiveTab('session')}
          >
            {t('training.sessionVideos', 'Session Videos')}
            {activeTab === 'session' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-t-full" />}
          </button>
          {canStats && (
            <button
              className={`pb-2.5 md:pb-3 px-3 md:px-1 text-xs md:text-sm font-medium transition-all relative rounded-t-lg ${activeTab === 'stats' ? 'text-blue-400 bg-blue-500/5 md:bg-transparent' : 'text-gray-500 hover:text-gray-300'}`}
              onClick={() => setActiveTab('stats')}
            >
              Player Stats
              {activeTab === 'stats' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-t-full" />}
            </button>
          )}
        </div>

        {activeTab === 'stats' ? (
          <PlayerStatsTab videos={videos} />
        ) : (
          <>
            {/* Mobile Clips List - Premium Design */}
            <div className="md:hidden">
              {/* Video Player */}
              <div className="mb-4">
                {currentVideo ? (
                  <>
                    <VideoPlayer
                      videoUrl={currentVideoUrl}
                      videoId={currentVideo.id}
                      teamId={session.team_id}
                      onEnded={handleVideoEnd}
                      annotations={annotations}
                      onAnnotationCreate={handleAnnotationCreate}
                      onDeleteAnnotation={handleDeleteAnnotation}
                      onPreviousVideo={handlePreviousVideo}
                      onNextVideo={handleNextVideo}
                    />
                    {isPlayingAll && (
                      <div className="flex justify-center mt-2">
                        <span className="text-[11px] text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full">
                          Playing {currentVideoIndex + 1} of {filteredVideos.length}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] border border-gray-800/50 rounded-xl p-10 text-center">
                    <Play className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">{t('videos.noVideos')}</p>
                  </div>
                )}
              </div>

              {/* Filters Toggle */}
              <div className="mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800/50 text-xs rounded-lg h-8 w-full justify-between px-3"
                >
                  <span className="flex items-center">
                    <Filter className="w-3.5 h-3.5 mr-1.5" />
                    {showFilters ? 'Filtreleri Kapat' : 'Filtrele'}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
              </div>

              {/* Filters */}
              <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showFilters ? 'max-h-[600px] opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                <div className="bg-[#111113] rounded-xl border border-gray-800/50 p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={filters.down} onValueChange={(v) => setFilters({ ...filters, down: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-800 text-white text-xs h-9 rounded-lg">
                        <SelectValue placeholder={t('training.down')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                        <SelectItem value="1">1st Down</SelectItem>
                        <SelectItem value="2">2nd Down</SelectItem>
                        <SelectItem value="3">3rd Down</SelectItem>
                        <SelectItem value="4">4th Down</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filters.formation} onValueChange={(v) => setFilters({ ...filters, formation: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-800 text-white text-xs h-9 rounded-lg">
                        <SelectValue placeholder={t('training.formation')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                        {formations.map(f => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filters.personnel} onValueChange={(v) => setFilters({ ...filters, personnel: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-800 text-white text-xs h-9 rounded-lg">
                        <SelectValue placeholder={t('training.personnel')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                        {personnels.map(p => (
                          <SelectItem key={p} value={p}>{p} Personnel</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filters.result} onValueChange={(v) => setFilters({ ...filters, result: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-800 text-white text-xs h-9 rounded-lg">
                        <SelectValue placeholder={t('training.result')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                        {results.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Premium Playlist */}
              <div className="rounded-xl bg-gradient-to-b from-[#131315] to-[#0e0e10] border border-gray-800/50 overflow-hidden shadow-2xl">
                {/* Playlist Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800/50 bg-[#161618]/50 backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                    <span className="text-white font-semibold text-sm tracking-wide">{t('videoDetail.clips')}</span>
                    <span className="text-[10px] text-gray-400 bg-gray-800/80 px-1.5 py-0.5 rounded-md font-medium border border-gray-700/50">{filteredVideos.length}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {selectedVideos.length > 0 && (
                      <Button size="sm" onClick={handleBulkTag} className="bg-purple-600 hover:bg-purple-700 h-7 text-[10px] px-2.5 rounded-md shadow-lg shadow-purple-900/20 font-medium">
                        <Tag className="w-3 h-3 mr-1" />
                        {selectedVideos.length}
                      </Button>
                    )}
                    <div className="flex bg-[#0a0a0b] p-0.5 rounded-lg border border-gray-800/50">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-1.5 rounded-md transition-all duration-200 ${viewMode === 'list' ? 'bg-blue-600/15 text-blue-400 shadow-inner' : 'text-gray-600 hover:text-gray-400'}`}
                      >
                        <LayoutList className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-1.5 rounded-md transition-all duration-200 ${viewMode === 'grid' ? 'bg-blue-600/15 text-blue-400 shadow-inner' : 'text-gray-600 hover:text-gray-400'}`}
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Playlist Items */}
                <div className="max-h-[55vh] overflow-y-auto overscroll-contain pb-6">
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 p-1">
                      {filteredVideos.map((video, index) => (
                        <div
                          key={video.id}
                          className={`relative aspect-video rounded-lg overflow-hidden bg-black cursor-pointer group transition-all duration-300 ${currentVideoIndex === index
                            ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[#0e0e10] z-10 scale-[1.02]'
                            : 'opacity-70 hover:opacity-100 hover:scale-[1.01]'
                            }`}
                          onClick={() => setCurrentVideoIndex(index)}
                        >
                          <img
                            src={(() => {
                              const thumb = video.video?.thumbnail_url || video.thumbnail_url;
                              if (!thumb) return '/placeholder-video.png';
                              if (thumb.startsWith('http') || thumb.startsWith('blob:')) return thumb;
                              return `${BACKEND_URL}${thumb}`;
                            })()}
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          {currentVideoIndex === index && (
                            <div className="absolute inset-0 flex items-center justify-center bg-blue-900/20 backdrop-blur-[1px]">
                              <div className="w-8 h-8 bg-blue-500/90 rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                                <Play className="w-4 h-4 text-white fill-current ml-0.5" />
                              </div>
                            </div>
                          )}
                          <div className="absolute bottom-1.5 right-1.5 bg-black/70 backdrop-blur-sm px-1.5 py-0.5 rounded text-[9px] text-white font-mono tracking-wider">
                            {formatTime(video.duration)}
                          </div>
                          <div className="absolute top-1.5 left-1.5 bg-black/50 backdrop-blur-sm w-4.5 h-4.5 rounded-full flex items-center justify-center text-[9px] text-gray-200 font-bold border border-white/10">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-800/40">
                      {filteredVideos.map((video, index) => (
                        <div
                          key={video.id}
                          className={`flex items-center gap-3 px-3 py-3 transition-all duration-300 cursor-pointer active:scale-[0.98] ${currentVideoIndex === index
                            ? 'bg-gradient-to-r from-blue-600/15 via-blue-600/5 to-transparent border-l-[3px] border-l-blue-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]'
                            : 'hover:bg-white/[0.03] border-l-[3px] border-l-transparent'
                            }`}
                          onClick={() => setCurrentVideoIndex(index)}
                        >
                          {/* Number / Playing Indicator */}
                          <div className="w-5 text-center shrink-0 flex justify-center">
                            {currentVideoIndex === index ? (
                              <div className="flex items-end justify-center gap-[2px] h-3.5">
                                <div className="w-[3px] bg-blue-500 rounded-t-sm animate-[bounce_1s_infinite_0ms]" style={{ height: '60%' }} />
                                <div className="w-[3px] bg-blue-400 rounded-t-sm animate-[bounce_1s_infinite_150ms]" style={{ height: '100%' }} />
                                <div className="w-[3px] bg-blue-500 rounded-t-sm animate-[bounce_1s_infinite_300ms]" style={{ height: '80%' }} />
                              </div>
                            ) : (
                              <span className="text-[11px] text-gray-500 font-semibold">{index + 1}</span>
                            )}
                          </div>

                          {/* Thumbnail */}
                          <div className={`w-[60px] h-[40px] rounded flex-shrink-0 relative overflow-hidden ${currentVideoIndex === index ? 'ring-1 ring-blue-500/50 shadow-lg shadow-blue-900/20' : 'ring-1 ring-gray-800/50'}`}>
                            <img
                              src={(() => {
                                const thumb = video.video?.thumbnail_url || video.thumbnail_url;
                                if (!thumb) return '/placeholder-video.png';
                                if (thumb.startsWith('http') || thumb.startsWith('blob:')) return thumb;
                                return `${BACKEND_URL}${thumb}`;
                              })()}
                              alt=""
                              className="w-full h-full object-cover bg-gray-900"
                            />
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center transition-colors">
                              <Play className={`w-3.5 h-3.5 transition-transform ${currentVideoIndex === index ? 'text-blue-400 fill-current scale-110' : 'text-white/70'}`} />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 py-0.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 pr-2">
                                {(video.play_data?.down || video.play_data?.play_type || video.play_data?.result) ? (
                                  <div className="flex flex-wrap gap-1.5 mb-1">
                                    {video.play_data?.down && (
                                      <span className="text-[9px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded shadow-sm">
                                        {video.play_data.down}&{video.play_data.distance}
                                      </span>
                                    )}
                                    {video.play_data?.play_type && (
                                      <span className="text-[9px] font-bold bg-orange-500/10 border border-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded shadow-sm">
                                        {video.play_data.play_type}
                                      </span>
                                    )}
                                    {video.play_data?.result && (
                                      <span className="text-[9px] font-bold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded shadow-sm">
                                        {video.play_data.result}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <p className={`text-xs font-semibold truncate mb-0.5 ${currentVideoIndex === index ? 'text-blue-100' : 'text-gray-300'}`}>
                                    Klip {index + 1}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 text-[10px] text-gray-500 font-medium">
                                  <span className="flex items-center gap-1 bg-gray-900/50 px-1.5 py-0.5 rounded">
                                    <Clock className="w-2.5 h-2.5" />
                                    <span className="font-mono">{formatTime(video.duration)}</span>
                                  </span>
                                  {video.play_data?.formation && (
                                    <span className="truncate flex-1">• {video.play_data.formation}</span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center shrink-0 bg-[#0a0a0b]/80 border border-gray-800/80 rounded-lg p-0.5 shadow-sm">
                                {canEdit && (
                                  <button
                                    className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-800 rounded-md transition-all active:scale-95"
                                    onClick={(e) => { e.stopPropagation(); setEditingVideo(video); }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                )}
                                {canStats && (
                                  <button
                                    className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-gray-800 rounded-md transition-all active:scale-95"
                                    onClick={(e) => { e.stopPropagation(); setStatsVideo(video); }}
                                  >
                                    <BarChart2 className="w-3 h-3" />
                                  </button>
                                )}
                                {canManage && (
                                  <button
                                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-800 rounded-md transition-all active:scale-95"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteClip(video.id); }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Layout (unchanged) */}
            <div className="hidden md:block">
              {/* Filters Toggle Button */}
              <div className="mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800/50"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {showFilters ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
                </Button>
              </div>

              {/* Filters */}
              <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showFilters ? 'max-h-[600px] opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
                <Card className="bg-[#1a1a1b] border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Filter className="w-5 h-5" />
                      {t('training.filters')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <Select value={filters.down} onValueChange={(v) => setFilters({ ...filters, down: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                          <SelectValue placeholder={t('training.down')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                          <SelectItem value="1">1st Down</SelectItem>
                          <SelectItem value="2">2nd Down</SelectItem>
                          <SelectItem value="3">3rd Down</SelectItem>
                          <SelectItem value="4">4th Down</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={filters.formation} onValueChange={(v) => setFilters({ ...filters, formation: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                          <SelectValue placeholder={t('training.formation')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                          {formations.map(f => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={filters.personnel} onValueChange={(v) => setFilters({ ...filters, personnel: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                          <SelectValue placeholder={t('training.personnel')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                          {personnels.map(p => (
                            <SelectItem key={p} value={p}>{p} Personnel</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={filters.result} onValueChange={(v) => setFilters({ ...filters, result: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                          <SelectValue placeholder={t('training.result')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('notes.allVideos')}</SelectItem>
                          {results.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Video Player */}
                <div className="lg:col-span-2">
                  {currentVideo ? (
                    <>
                      <VideoPlayer
                        videoUrl={currentVideoUrl}
                        videoId={currentVideo.id}
                        teamId={session.team_id}
                        onEnded={handleVideoEnd}
                        annotations={annotations}
                        onAnnotationCreate={handleAnnotationCreate}
                        onDeleteAnnotation={handleDeleteAnnotation}
                        onPreviousVideo={handlePreviousVideo}
                        onNextVideo={handleNextVideo}
                      />
                      <div className="flex justify-between items-center mt-2 px-2">
                        {isPlayingAll && (
                          <p className="text-sm text-gray-400">
                            Playing {currentVideoIndex + 1} of {filteredVideos.length}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-12 text-center">
                      <p className="text-gray-400">{t('videos.noVideos')}</p>
                    </div>
                  )}
                </div>

                {/* Clips List */}
                <div>
                  <Card className="bg-[#1a1a1b] border-gray-800">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white">{t('videoDetail.clips')} ({filteredVideos.length})</CardTitle>
                        <div className="flex items-center gap-2">
                          {selectedVideos.length > 0 && (
                            <Button size="sm" onClick={handleBulkTag} className="bg-purple-600 hover:bg-purple-700 h-8">
                              <Tag className="w-3 h-3 mr-1" />
                              {selectedVideos.length}
                            </Button>
                          )}
                          <div className="flex bg-[#0a0a0b] p-1 rounded-md border border-gray-800">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewMode('list')}
                              className={`h-7 w-7 p-0 ${viewMode === 'list' ? 'bg-gray-800 text-blue-400' : 'text-gray-500'}`}
                            >
                              <LayoutList className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewMode('grid')}
                              className={`h-7 w-7 p-0 ${viewMode === 'grid' ? 'bg-gray-800 text-blue-400' : 'text-gray-500'}`}
                            >
                              <LayoutGrid className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                      {viewMode === 'grid' ? (
                        <div className="grid grid-cols-3 gap-2">
                          {filteredVideos.map((video, index) => (
                            <div
                              key={video.id}
                              className={`relative aspect-video rounded-md overflow-hidden bg-black border cursor-pointer group transition-all ${currentVideoIndex === index
                                ? 'border-blue-500 ring-1 ring-blue-500'
                                : 'border-gray-800 hover:border-gray-600'
                                }`}
                              onClick={() => setCurrentVideoIndex(index)}
                            >
                              <img
                                src={(() => {
                                  const thumb = video.video?.thumbnail_url || video.thumbnail_url;
                                  if (!thumb) return '/placeholder-video.png';
                                  if (thumb.startsWith('http') || thumb.startsWith('blob:')) return thumb;
                                  return `${BACKEND_URL}${thumb}`;
                                })()}
                                alt=""
                                className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                              />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                                <Play className={`w-8 h-8 ${currentVideoIndex === index ? 'text-blue-500 fill-current' : 'text-white'}`} />
                              </div>
                              <div className="absolute top-1 left-1">
                                <Checkbox
                                  checked={selectedVideos.includes(video.id)}
                                  onCheckedChange={() => toggleVideoSelection(video.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                />
                              </div>
                              <div className="absolute bottom-1 right-1 bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-white font-mono">
                                {formatTime(video.duration)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredVideos.map((video, index) => (
                            <div
                              key={video.id}
                              className={`p-3 rounded-lg transition-all cursor-pointer group ${currentVideoIndex === index
                                ? 'bg-blue-600/20 border border-blue-600'
                                : 'bg-[#0a0a0b] hover:bg-gray-800/50'
                                }`}
                              onClick={() => setCurrentVideoIndex(index)}
                            >
                              <div className="flex items-center gap-4">
                                <Checkbox
                                  checked={selectedVideos.includes(video.id)}
                                  onCheckedChange={() => toggleVideoSelection(video.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="border-gray-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                />
                                <div className={`w-12 h-12 rounded bg-gray-900 border border-gray-800 flex items-center justify-center flex-shrink-0 group-hover:border-blue-500/50 transition-colors ${currentVideoIndex === index ? 'border-blue-500/50' : ''}`}>
                                  <Play className={`w-5 h-5 ${currentVideoIndex === index ? 'text-blue-500 fill-current' : 'text-gray-600 group-hover:text-blue-400'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1.5 gap-2">
                                    <div className="flex flex-wrap gap-1.5">
                                      {video.play_data?.down && (
                                        <span className="text-[10px] font-bold bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                                          {video.play_data.down} & {video.play_data.distance}
                                        </span>
                                      )}
                                      {video.play_data?.play_type && (
                                        <span className="text-[10px] font-bold bg-orange-600/20 text-orange-400 px-2 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                                          {video.play_data.play_type}
                                        </span>
                                      )}
                                      {video.play_data?.result && (
                                        <span className="text-[10px] font-bold bg-green-600/20 text-green-400 px-2 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                                          {video.play_data.result}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-0.5 shrink-0 self-start">
                                      {canEdit && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-white hover:bg-gray-800" onClick={(e) => { e.stopPropagation(); setEditingVideo(video); }}>
                                          <Pencil className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                      {canStats && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-blue-400 hover:bg-gray-800" onClick={(e) => { e.stopPropagation(); setStatsVideo(video); }}>
                                          <BarChart2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                      {canManage && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-red-500 hover:bg-gray-800" onClick={(e) => { e.stopPropagation(); handleDeleteClip(video.id); }}>
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {formatTime(video.duration)}
                                    </span>
                                    {video.play_data?.formation && (
                                      <span className="flex items-center gap-1 before:content-['•'] before:mr-1">
                                        {video.play_data.formation}
                                      </span>
                                    )}
                                    {video.play_data?.personnel && (
                                      <span className="flex items-center gap-1 before:content-['•'] before:mr-1">
                                        {video.play_data.personnel}p
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Add Clip Modal - Now with Upload */}
        <Dialog open={showAddClipModal} onOpenChange={setShowAddClipModal}>
          <DialogContent className="w-full max-w-md mx-auto sm:max-w-[600px] p-0 overflow-hidden bg-[#121214] border-t border-gray-800 sm:border rounded-t-[2rem] sm:rounded-xl mt-auto mb-0 sm:my-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col text-white">
            <div className="bg-gradient-to-r from-blue-900/40 via-indigo-900/30 to-[#121214] border-b border-gray-800/60 p-5 sm:p-6 sticky top-0 z-20 backdrop-blur-xl shrink-0">
              <div className="w-12 h-1.5 bg-gray-800 rounded-full mx-auto mb-4 sm:hidden" />
              <DialogHeader className="flex flex-row items-center justify-between space-y-0">
                <DialogTitle className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-300">
                  Upload Clip to Session
                </DialogTitle>
              </DialogHeader>
            </div>
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 custom-scrollbar">
              <UploadClipForm
                sessionId={id}
                onSuccess={async () => {
                  const videosRes = await trainingAPI.listSessionVideos(id);
                  setVideos(videosRes.data);
                  setFilteredVideos(videosRes.data);
                  setShowAddClipModal(false);
                }}
                onCancel={() => setShowAddClipModal(false)}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Clip Modal */}
        {editingVideo && (
          <EditClipModal
            session={session}
            video={editingVideo}
            isOpen={!!editingVideo}
            onClose={() => setEditingVideo(null)}
            onSuccess={handleSuccessUpdate}
          />
        )}

        {/* Player Stats Modal */}
        {statsVideo && (
          <PlayerStatsModal
            session={session}
            video={statsVideo}
            isOpen={!!statsVideo}
            onClose={() => setStatsVideo(null)}
            onSuccess={async () => {
              const videosRes = await trainingAPI.listSessionVideos(id);
              setVideos(videosRes.data);
              setFilteredVideos(videosRes.data);
            }}
          />
        )}
      </div>
    </TeamGate >
  );
};

export default TrainingSession;
